import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { isApiError } from '../api/client';
import { authApi } from '../api/endpoints';
import { meQuery } from '../api/queries';
import type { Me } from '../api/types';
import { BATCH_SESSION_PREFIX, clearSessionPrefix } from '../lib/storage';
import { useToast } from '../ui/Toast';

/** The signed-in user, or null when there is no valid session (401). Other errors propagate. */
export async function loadMe(queryClient: QueryClient): Promise<Me | null> {
  try {
    return await queryClient.ensureQueryData(meQuery);
  } catch (error) {
    if (isApiError(error) && error.status === 401) return null;
    throw error;
  }
}

/** Only same-app paths are accepted as post-login destinations (no open redirects). */
export function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return undefined;
  if (value.startsWith('/login') || value.startsWith('/register')) return undefined;
  return value;
}

export function useSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: async () => {
      queryClient.clear();
      // A batch (WP-1b.3) belongs to the signed-in user: it leaves with the session.
      clearSessionPrefix(BATCH_SESSION_PREFIX);
      await navigate({ to: '/login' });
    },
    onError: (error) => toast.error(error),
  });
}
